import { getEntityDisplayName } from '@agilelab/plugin-wb-builder-common';
import {
  asEntity,
  renderMessageWithSeverity,
  WbTextField,
} from '@agilelab/plugin-wb-platform';
import { identityApiRef, useApi } from '@mainstage/core-plugin-api';
import { catalogApiRef } from '@mainstage/plugin-catalog-react';
import { useTheme } from '@material-ui/core';
import { JSONSchema7 } from 'json-schema';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fromZodError } from 'zod-validation-error';
import { useFormExtraConfigContext } from '../../../contexts/FormExtraConfigContext';
import { FieldExtensionUiSchema } from '../../../extensions/types';
import { extractCustomProperties, isHidden } from '../../utils';
import {
  buildResolvedObject,
  resolveEntities,
} from './EntitySelectionPicker.utils';
import {
  EntitySelectionPickerProps,
  EntitySelectionPickerSchema,
  EntitySelectionPickerSchemaZod,
} from './schema';
import { extractFieldPathFromRjsfId } from '../../MultiStepJsonForm/utils/extractFieldPathFromRjsfId';
import { DEPENDENCY_MANAGER_ID_SEPARATOR } from '../../MultiStepJsonForm/utils';

// Module-level cache for tracking registered field paths per middleware
// This prevents re-registration when components mount/unmount (e.g., during pagination)
// But allows re-registration when middleware is recreated
const registeredFieldPaths = new Map<string, Set<string>>();

// Module-level cache for resolved entities and credentials
// When 44 items depend on the same field, they all resolve the same entities
// This cache prevents 44 redundant network calls
interface CachedResolution {
  entitiesPromise: Promise<Record<string, any>[]>;
  expiresAt: number;
}
const entityResolutionCache = new Map<string, CachedResolution>();
const credentialCache = {
  token: undefined as string | undefined,
  expiresAt: 0,
};

const CACHE_TTL = 5000; // 5 seconds

export const allowArbitraryValues = (
  uiSchema: FieldExtensionUiSchema<string, any>,
  schema: JSONSchema7,
): boolean => {
  const configValue =
    (uiSchema['ui:options']?.allowArbitraryValues as boolean) ?? false;

  // even if set to true in the config, this works only if the type of the schema is string
  return configValue && schema.type === 'string';
};

function convertFieldNameValueIntoArray(
  fieldNameValue: any,
): (Record<string, any> | string)[] {
  if (!Array.isArray(fieldNameValue)) {
    return [fieldNameValue];
  }
  return fieldNameValue;
}

export const EntitySelectionPicker = (props: EntitySelectionPickerProps) => {
  const {
    onChange,
    schema,
    required,
    uiSchema,
    rawErrors,
    formData,
    formContext,
    idSchema,
  } = props;
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const theme = useTheme();
  const [pickerConfig, setPickerConfig] = useState<
    EntitySelectionPickerSchema | undefined
  >(undefined);
  const [pickerMessages, setPickerMessages] = useState<
    Map<string, { line: string; severity: 'info' | 'warning' | 'error' }>
  >(new Map([]));
  const customProps = extractCustomProperties(uiSchema);

  // Check if dependency manager is available
  const dependencyManager = formContext?.dependencyManager;
  const rootSchema = formContext?.rootSchema;

  // Extract canonical field path
  // Convert from RJSF's underscore separator to dependency manager's > separator
  const fieldPath = useMemo(() => {
    const id = idSchema?.$id || '';
    const path = extractFieldPathFromRjsfId(id, rootSchema);
    return path;
  }, [idSchema, rootSchema]);

  const manualEditEnabled = allowArbitraryValues(uiSchema, schema);

  // Memoize getPropertyValues - only recreate if pickerConfig or schema.default changes
  const getPropertyValues = useCallback(
    (obj: Record<string, any>): Record<string, any> => {
      // Check if ui:property is set
      if (pickerConfig && pickerConfig['ui:property']) {
        return _.get(obj, pickerConfig['ui:property']) ?? schema.default;
      }

      // Check if mapTo is set
      if (pickerConfig && pickerConfig.mapTo) {
        return buildResolvedObject(
          obj,
          pickerConfig.mapTo as Record<string, any> | '.',
        );
      }

      return obj;
    },
    [pickerConfig, schema.default],
  );

  // Memoize resolvePickerValue - only recreate if schema.type changes
  const resolvePickerValue = useCallback(
    (propertyRefs: (Record<string, any> | string | undefined)[]): any => {
      if (propertyRefs.length === 1 && schema.type !== 'array') {
        return propertyRefs[0];
      }

      return propertyRefs;
    },
    [schema.type],
  );

  const resolveObject = useCallback(
    (obj: Record<string, any> | string): string | undefined => {
      if (typeof obj === 'string') {
        return obj;
      }
      if (pickerConfig?.['ui:displayLabel']) {
        return _.get(obj, pickerConfig!['ui:displayLabel']!);
      }
      const entity = asEntity(obj);
      if (entity) {
        return getEntityDisplayName(entity);
      }
      return undefined;
    },
    [pickerConfig],
  );

  // Use useEffect to register once per fieldPath + middleware combination
  // This prevents re-registration when formContext or formData change
  useEffect(() => {
    if (!dependencyManager || !pickerConfig?.['ui:fieldName']) {
      return;
    }

    // Track if we've already registered for this specific fieldPath and middleware
    // Use a module-level Map to prevent multiple EntitySelectionPicker instances from re-registering
    // The Map is keyed by middleware ID, so when middleware is recreated, we re-register
    const middlewareId = dependencyManager?.id || 'default';

    // Get or create the Set for this middleware
    if (!registeredFieldPaths.has(middlewareId)) {
      registeredFieldPaths.set(middlewareId, new Set<string>());
    }
    const middlewareRegistrations = registeredFieldPaths.get(middlewareId)!;

    // Check if this is an array field (fieldPath contains numeric indices) or a simple field
    const pathSegments = fieldPath.split(DEPENDENCY_MANAGER_ID_SEPARATOR);
    const isArrayField = pathSegments.some(seg => /^\d+$/.test(seg));

    // For non-array fields, use strict caching to prevent re-registration
    // For array fields, we need to allow re-registration of handlers for new items
    // (the cache is only used for template dependency registration)
    const alreadyRegistered = middlewareRegistrations.has(fieldPath);
    if (alreadyRegistered && !isArrayField) {
      return;
    }

    // Capture formContext at registration time for array length calculation
    const initialFormContext = formContext;

    // Register dependencies: this field depends on the field specified in ui:fieldName
    // For array items, use template path and only register once per template
    if (
      pickerConfig?.['ui:fieldName'] &&
      dependencyManager?.registerDependencies
    ) {
      if (isArrayField) {
        // For array items, register dependencies using TEMPLATE PATH with []
        // Example: "dataAssets>0>physicalAsset>sourceTable>catalogName" → "dataAssets>[]>physicalAsset>sourceTable>catalogName"
        // This allows the dependency manager to find all items when the dependency changes

        // Convert the specific path to a template path by replacing numeric indices with []
        const templatePath = pathSegments
          .map(seg => (/^\d+$/.test(seg) ? '[]' : seg))
          .join(DEPENDENCY_MANAGER_ID_SEPARATOR);

        // Only register template dependency once (use template path for caching)
        if (!middlewareRegistrations.has(templatePath)) {
          dependencyManager.registerDependencies(templatePath, [
            pickerConfig['ui:fieldName'],
          ]);
          middlewareRegistrations.add(templatePath);
        }
      } else {
        // Non-array case: register for the specific field
        dependencyManager.registerDependencies(fieldPath, [
          pickerConfig['ui:fieldName'],
        ]);
      }
    }

    // Mark this specific fieldPath as registered (for non-array fields, this prevents re-entry above)
    middlewareRegistrations.add(fieldPath);

    // Register handlers for all paths
    // Create the handler function once (shared by all paths)
    // IMPORTANT: This is NOT async. It returns a plain value for synchronous
    // extractions (object → property) and a Promise only when entity resolution
    // via catalog API is needed. This distinction is critical because
    // processUpdate uses `instanceof Promise` to decide whether to update
    // workingFormData synchronously. If sync extractions were wrapped in
    // Promises (due to `async`), downstream handlers in the same cascade
    // would see stale formData.
    const createHandler = () => {
      return (
        handlerFieldPath: string,
        triggerPath: string,
        triggerValue: any,
        handlerFormData: any,
      ): any => {
        const dependencyPath = pickerConfig!['ui:fieldName']!;

        // Determine the relationship between trigger and our declared dependency:
        // 1. Direct trigger: triggerPath === dependencyPath (e.g., "dataproduct" triggers developmentGroup)
        // 2. Sub-field trigger: triggerPath starts with dependencyPath + separator (e.g., "sourceSchema>catalogName" triggers sourceTable.catalogName)
        // 3. Indirect trigger: triggerPath is unrelated (e.g., "domain" triggers developmentGroup via dependency chain)
        const isDirectTrigger = triggerPath === dependencyPath;
        const isSubFieldTrigger = triggerPath.startsWith(
          `${dependencyPath}${DEPENDENCY_MANAGER_ID_SEPARATOR}`,
        );

        let referencedValue: any;

        if (isDirectTrigger) {
          // Direct trigger: use triggerValue directly
          referencedValue = triggerValue;
        } else if (isSubFieldTrigger) {
          // Sub-field trigger: read the full object from formData
          // Example: sourceSchema.catalogName changed, but we need the full sourceSchema object
          // IMPORTANT: formData might have the OLD value (due to async state update),
          // so we need to patch it with the new trigger value
          referencedValue = _.get(handlerFormData, dependencyPath);

          if (referencedValue && typeof referencedValue === 'object') {
            // Get the sub-field path relative to dependency path (e.g., "catalogName" from "sourceSchema>catalogName")
            const subFieldPath = triggerPath
              .slice(dependencyPath.length + 1)
              .replace(new RegExp(DEPENDENCY_MANAGER_ID_SEPARATOR, 'g'), '.');
            // Create a copy with the updated sub-field
            referencedValue = _.cloneDeep(referencedValue);
            _.set(referencedValue, subFieldPath, triggerValue);
          }
        } else {
          // Indirect trigger via dependency chain (e.g., domain → dataproduct → developmentGroup)
          // Read from formData, but if dependency is not set yet, preserve current value
          referencedValue = _.get(handlerFormData, dependencyPath);

          if (!referencedValue) {
            // Dependency hasn't been set yet (user needs to select it first)
            // Preserve the current value of this field
            return _.get(handlerFormData, handlerFieldPath);
          }
        }

        if (!referencedValue) {
          return schema.default;
        }

        // Check if referencedValue is a plain object (not an entity reference string)
        // If it's an object, extract the property directly without resolving entities
        // This is a SYNCHRONOUS path — return plain value, not Promise
        if (
          typeof referencedValue === 'object' &&
          !Array.isArray(referencedValue)
        ) {
          // Direct property extraction from object
          const extractedValue = getPropertyValues(referencedValue);
          const result = resolvePickerValue([extractedValue]);
          return result;
        }

        // Entity resolution via catalog API — ASYNC path, return Promise
        const resolveAsync = async () => {
          try {
            // Cache credentials to avoid 44 identical getCredentials() calls
            let token = credentialCache.token;
            if (!token || Date.now() > credentialCache.expiresAt) {
              const credentials = await identityApi.getCredentials();
              token = credentials.token;
              credentialCache.token = token;
              credentialCache.expiresAt = Date.now() + CACHE_TTL;
            }

            // Cache entity resolution: if 44 items depend on the same field value,
            // they all resolve the same entities - do it only once
            const cacheKey = JSON.stringify(
              convertFieldNameValueIntoArray(referencedValue),
            );
            let cachedResolution = entityResolutionCache.get(cacheKey);

            // Check if cache is still valid
            if (!cachedResolution || Date.now() > cachedResolution.expiresAt) {
              const entitiesPromise = resolveEntities(
                convertFieldNameValueIntoArray(referencedValue),
                { catalogApi, token },
              );
              cachedResolution = {
                entitiesPromise,
                expiresAt: Date.now() + CACHE_TTL,
              };
              entityResolutionCache.set(cacheKey, cachedResolution);
            }

            const entities = await cachedResolution.entitiesPromise;

            if (entities.length) {
              const propertyValues = entities.flatMap(getPropertyValues);
              const result = resolvePickerValue(propertyValues);
              return result;
            }
          } catch (error) {
            // Fail silently - return default value
          }
          return schema.default;
        };

        return resolveAsync();
      };
    };

    if (isArrayField) {
      // For array items, always register handler for the CURRENT item
      // Extract the current item index from fieldPath
      const currentIndex = parseInt(pathSegments[1], 10);

      if (!isNaN(currentIndex)) {
        dependencyManager.registerHandler(fieldPath, createHandler());
      }

      // Also register handlers for OTHER items if they exist in the array
      const arrayName = pathSegments[0];
      let arrayLength = 0;

      if (initialFormContext && typeof initialFormContext === 'object') {
        const array = _.get(initialFormContext, arrayName);
        if (Array.isArray(array)) {
          arrayLength = array.length;
        }
      }

      for (let i = 0; i < arrayLength; i++) {
        if (i === currentIndex) continue; // Skip current, already registered

        const itemPath = pathSegments
          .map((seg, idx) => (idx === 1 ? i.toString() : seg))
          .join(DEPENDENCY_MANAGER_ID_SEPARATOR);

        dependencyManager.registerHandler(itemPath, createHandler());
      }
    } else {
      // Non-array case: register for the specific field
      dependencyManager.registerHandler(fieldPath, createHandler());
    }

    // NOTE: Do NOT unregister handlers in cleanup
    // Handlers must persist for the entire form lifetime because:
    // 1. All 44 array items share the same handler (for the dependency)
    // 2. When pagination changes, the component mounts/unmounts on different pages
    // 3. Unregistering would break handlers for items on non-rendered pages
    // 4. The dependency manager will handle cleanup when the form unmounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dependencyManager,
    fieldPath,
    pickerConfig, // Include pickerConfig to re-run when it's parsed
    // NOTE: getPropertyValues, resolvePickerValue are NOT in dependencies
    // because they change reference on every render, causing infinite re-execution.
    // We capture them at execution time instead.
    catalogApi,
    identityApi,
    schema.default,
  ]);

  /**
   * Checks if there are misconfiguration while setting up the EntitySelectionPicker
   */
  useEffect(() => {
    const parsedPickerConfig = EntitySelectionPickerSchemaZod.safeParse({
      ...uiSchema,
      ...schema,
    });
    if (!parsedPickerConfig.success) {
      const parsingErrorMessage = `Error while trying to parse EntitySelectionPicker configurations: ${fromZodError(
        parsedPickerConfig.error,
      ).message.toLowerCase()}. Please contact the platform team.`;
      setPickerMessages(existingErrors =>
        existingErrors.set('parsing_error', {
          line: parsingErrorMessage,
          severity: 'error',
        }),
      );
      return;
    }
    setPickerMessages(existingErrors => {
      existingErrors.delete('parsing_error');
      return existingErrors;
    });
    setPickerConfig(parsedPickerConfig.data);
  }, [uiSchema, schema]);

  /**
   * Normalize formData: if it's an entity object, extract the property value
   * This handles the case where initial formData contains a full entity object
   * Only memoize based on actual changes to formData value, not fieldPath
   */
  const normalizedFormData = useMemo(() => {
    if (
      typeof formData === 'string' ||
      formData === null ||
      formData === undefined
    ) {
      return formData;
    }

    // If formData is an object and looks like an entity (has kind and metadata), try to extract the property
    if (
      typeof formData === 'object' &&
      (formData as any).kind &&
      (formData as any).metadata &&
      pickerConfig &&
      pickerConfig['ui:property']
    ) {
      const extractedValue = _.get(formData, pickerConfig['ui:property']);
      if (extractedValue !== undefined) {
        return extractedValue;
      }
    }

    return formData;
  }, [formData, pickerConfig]);

  /**
   * Extract the display value from formData
   * - If formData is a string, use it directly
   * - If formData is an object (entity), extract the label
   */
  const inputValue = useMemo(() => {
    if (
      typeof normalizedFormData === 'string' ||
      normalizedFormData === null ||
      normalizedFormData === undefined
    ) {
      return normalizedFormData;
    }

    // normalizedFormData is an object - extract label
    return resolveObject(normalizedFormData);
  }, [normalizedFormData, resolveObject]);

  const { debounceMs } = useFormExtraConfigContext();

  // Handle manual changes
  const handleChange = useCallback(
    (value: string) => {
      // Notify dependency manager or fallback to onChange
      if (dependencyManager?.notifyUpdate && fieldPath) {
        dependencyManager.notifyUpdate({
          fieldPath,
          value,
          source: 'user',
          intent: 'manual',
        });
      } else {
        onChange(value);
      }
    },
    [dependencyManager, fieldPath, onChange],
  );

  return (
    <>
      <WbTextField
        label={schema.title}
        style={{
          display: isHidden(uiSchema) ? 'none' : undefined,
          width: '100%',
        }}
        inputProps={{ style: { width: '100%' } }}
        error={rawErrors?.length > 0 && !formData}
        disabled={!manualEditEnabled}
        helperText={
          pickerMessages.size
            ? renderMessageWithSeverity(
                Array.from(pickerMessages.values()),
                theme,
              )
            : schema.description
        }
        required={required}
        debounceMs={debounceMs}
        onChange={({ target: { value } }) => handleChange(value)}
        value={inputValue ?? ''}
        {...customProps}
      />
    </>
  );
};
